import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { useChatStore } from './store/chatStore';
import { useConfigStore } from './store/configStore';
import { AvailableLanguage, BotChatMessage, ChatWidgetConfig } from './types';

const isDev = import.meta.env.MODE === 'development';
// if (isDev) {
//   // Use dynamic import with then() instead of static import
//   void import('./index.scss').then(() => {
//     console.log('index.scss loaded');
//   });
// }

import './index.scss';

// Export function to render the chat widget
export const render1648ChatWidget = function (config: ChatWidgetConfig, container: HTMLElement) {
  // Create container
  let reactRoot = container;

  if (!reactRoot || isDev) {
    reactRoot = document.createElement('div');
    reactRoot.id = 'chat-widget-root';
    document.body.appendChild(reactRoot);
  }

  createRoot(reactRoot).render(
    <StrictMode>
      <App appConfig={config} />
    </StrictMode>
  );
};

window.init1648ChatWidget = function (config: ChatWidgetConfig) {
  // Create container
  const widgetContainer = document.createElement('div');
  widgetContainer.style.position = 'fixed';
  widgetContainer.style.top = '0';
  widgetContainer.style.left = '0';
  widgetContainer.style.zIndex = '1000';
  widgetContainer.style.width = '100vw';
  widgetContainer.style.height = '100vh';
  widgetContainer.style.pointerEvents = 'none';
  widgetContainer.id = 'chat-widget-container';

  // Create Shadow DOM
  const shadowRoot = widgetContainer.attachShadow({ mode: 'open' });

  // Load Tailwind styles into Shadow DOM and render widget only after styles are loaded
  fetch(config.cssUrl)
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to load CSS: ${response.status} ${response.statusText}`);
      }
      return response.text();
    })
    .then(cssText => {
      // Add Tailwind styles to Shadow DOM
      const styleElement = document.createElement('style');
      styleElement.textContent = cssText;
      shadowRoot.appendChild(styleElement);

      console.log('Tailwind styles loaded into Shadow DOM');

      // Base styles
      const baseStyles = document.createElement('style');
      baseStyles.textContent = `
        #chat-widget-root {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          overflow: hidden;
          pointer-events: none;
        }
      `;
      shadowRoot.appendChild(baseStyles);

      // Create root div for React
      const reactRoot = document.createElement('div');
      reactRoot.id = 'chat-widget-root';
      shadowRoot.appendChild(reactRoot);

      // Add container to page
      document.body.appendChild(widgetContainer);

      // Set data attributes for configuration
      Object.keys(config).forEach(key => {
        if (key !== 'cssUrl' && key !== 'requestHeaders') {
          reactRoot.dataset[key as keyof ChatWidgetConfig] = config[
            key as keyof ChatWidgetConfig
          ] as string;
        }
      });

      console.log('Rendering chat widget with config:', config);

      // Validate webhook URL
      if (config.webhookUrl) {
        try {
          new URL(config.webhookUrl); // Check URL validity
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
          console.log(e);
          console.warn('Invalid webhookUrl provided:', config.webhookUrl);
        }
      }

      // Render the chat widget only after styles are loaded
      const renderWidget = () => {
        render1648ChatWidget(config, reactRoot);
        console.log('Chat widget rendered after styles loaded');
      };

      // Initialize widget after page load
      if (document.readyState === 'complete') {
        renderWidget();
      } else {
        window.addEventListener('load', renderWidget);
      }
    })
    .catch(error => {
      console.error('Failed to load Tailwind styles:', error);
      // Widget will not be rendered if styles fail to load
    });
};

window.open1648ChatWidget = function (messageWhileOpen: string | null) {
  try {
    if (messageWhileOpen) {
      useChatStore.getState().setMessageWhileOpen(messageWhileOpen);
    }
    useChatStore.getState().setOpen(true);
  } catch {
    console.warn('Chat widget not initialized yet.');
  }
};

window.addMessage1648ChatWidget = function (botMessage: BotChatMessage) {
  try {
    useChatStore.getState().addExternalMessage(botMessage);
  } catch {
    console.warn('Chat widget not initialized yet or invalid message format.');
  }
};

const attachChatWidgetOpen = function (target: HTMLElement | string) {
  let element: HTMLElement | null = null;
  if (typeof target === 'string') {
    element = document.querySelector(target);
  } else if (target instanceof HTMLElement) {
    element = target;
  }
  if (element) {
    element.addEventListener('click', () => {
      const dataset = element.dataset;
      const messageWhileOpen = dataset.sendMessage;

      window.open1648ChatWidget(messageWhileOpen || null);
    });
  }
};

const handleSetChatWidgetLanguage = function (language: string) {
  console.log('handleSetChatWidgetLanguage', language);
  if (language !== 'en' && language !== 'de' && language !== 'fr' && language !== 'nl') {
    console.warn(`Unsupported language provided to chat widget: ${language}`);
    return;
  }

  try {
    useConfigStore.getState().setLanguage(language as AvailableLanguage);
  } catch (error) {
    console.warn('Chat widget not initialized yet. Unable to change language.', error);
  }
};

const handleSetChatWidgetContextSuggestions = function (
  contextSuggestions: Record<
    string,
    { suggestion: string; chatQuestion?: string; quickAnswer?: string; description?: string }
  >
) {
  try {
    useConfigStore.getState().setContextSuggestions(contextSuggestions);
  } catch (error) {
    console.warn('Chat widget not initialized yet. Unable to set context suggestions.', error);
  }
};

const chatWidget1648API = {
  attachChatWidgetOpen,
  addMessage: (botMessage: BotChatMessage) => window.addMessage1648ChatWidget(botMessage),
  setLanguage: (language: AvailableLanguage) => handleSetChatWidgetLanguage(language),
  updateConfig: (configOverrides: Partial<ChatWidgetConfig>) => {
    try {
      useConfigStore.getState().updateConfig(configOverrides);
    } catch (error) {
      console.warn('Chat widget not initialized yet. Unable to update config.', error);
    }
  },
  setContextSuggestions: (
    contextSuggestions: Record<
      string,
      { suggestion: string; chatQuestion?: string; quickAnswer?: string; description?: string }
    >
  ) => handleSetChatWidgetContextSuggestions(contextSuggestions),
} as const;

window.chatWidget1648API = chatWidget1648API;

declare global {
  interface Window {
    render1648ChatWidget: (config: ChatWidgetConfig, container: HTMLElement) => void;
    init1648ChatWidget: (config: ChatWidgetConfig) => void;
    open1648ChatWidget: (messageWhileOpen: string | null) => void;
    addMessage1648ChatWidget: (botMessage: BotChatMessage) => void;
    chatWidget1648API: typeof chatWidget1648API;
  }
}

// Notification about readiness
console.log('1648 Chat Widget renderer is initialized');
